import React, { useState } from "react";
import styles from "./InviteDialog.module.css";
import { defaultInviteOption } from "../../utils/sectionPermissions";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  FormControl,
  Grid,
  Box,
  CircularProgress,
  Select,
  MenuItem,
  Card,
} from "@mui/material";

const InviteDialog = ({
  showInviteDialog,
  inviteUserCloseHandler,
  trigger,
  setTrigger,
}) => {
  const [emailError, setEmailError] = useState(false);
  const [postLoader, setPostLoader] = useState(false);
  const formInitialState = {
    email: "",
    firstName: "",
    lastName: "",
    password: "",
    role: "admin",
    // Section-based permissions — one toggle per sidebar section,
    // not per-route. See utils/sectionPermissions.js for the SECTIONS
    // list and how these get matched at render time.
    option: defaultInviteOption(),
  };
  const [formData, setFormData] = React.useState(formInitialState);
  const handleNameChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  const handleRoleChange = (e) => {
    setFormData({ ...formData, role: e.target.value });
  };
  const handleOptionRoleChange = (i, val) => {
    let temp = [...formData.option];
    temp[i] = { ...temp[i], role: val };
    // When the inviter flips a section off, cascade items to noallow.
    // When flipping on, leave items at their current values — the
    // inviter then manually toggles individual sub-items they want.
    if (val !== "allow" && Array.isArray(temp[i].items)) {
      temp[i].items = temp[i].items.map((it) => ({ ...it, role: "noallow" }));
    }
    setFormData({ ...formData, option: temp });
  };

  const handleItemRoleChange = (sectionIdx, itemIdx, val) => {
    let temp = [...formData.option];
    const section = { ...temp[sectionIdx] };
    if (!Array.isArray(section.items)) return;
    section.items = section.items.map((it, j) =>
      j === itemIdx ? { ...it, role: val } : it,
    );
    // Convenience: if any item gets allowed while section is off, flip
    // section on too — otherwise canSee blocks the item.
    if (val === "allow" && section.role !== "allow") {
      section.role = "allow";
    }
    temp[sectionIdx] = section;
    setFormData({ ...formData, option: temp });
  };

  const handleSubmit = () => {
    setPostLoader(true);
    if (!formData.email.trim()) {
      setEmailError(true);
      return;
    }
    setEmailError(false);

    fetch(`${process.env.REACT_APP_SERVER_URL}/invite_user`, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "include",
      method: "POST",
      body: JSON.stringify(formData),
    }).then(() => {
      inviteUserCloseHandler();
      setTrigger(!trigger);
      setFormData(formInitialState);
      setPostLoader(false);
    });
  };

  const generatePassword = () => {
    const length = 12;
    const charset =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+-=[]{}|;:,.<>?";
    let password = "";
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      password += charset[randomIndex];
    }
    return password;
  };

  return (
    <>
      <Dialog
        open={showInviteDialog}
        onClose={inviteUserCloseHandler}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Invite User</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2}>
            <div>
              <Typography>Email</Typography>
              <TextField
                name="email"
                placeholder="Enter email"
                fullWidth
                value={formData.email}
                onChange={handleNameChange}
                required={true}
                error={emailError}
                helperText={emailError ? "Email is required" : ""}
              />
            </div>
            <div>
              <Typography>First Name</Typography>
              <TextField
                name="firstName"
                placeholder="Enter First Name"
                fullWidth
                value={formData.firstName}
                onChange={handleNameChange}
                required={true}
                error={emailError}
                helperText={emailError ? "Email is required" : ""}
              />
            </div>
            <div>
              <Typography>Last Name</Typography>
              <TextField
                name="lastName"
                placeholder="Enter Last Name"
                fullWidth
                value={formData.lastName}
                onChange={handleNameChange}
                required={true}
                error={emailError}
                helperText={emailError ? "Email is required" : ""}
              />
            </div>
            <div>
              <Typography>Password</Typography>
              <TextField
                name="password"
                placeholder="Password"
                fullWidth
                value={formData.password}
                onChange={handleNameChange}
                required={true}
                error={emailError}
                helperText={emailError ? "Email is required" : ""}
              />
              <button
                type="button"
                className={styles.generateBtn}
                onClick={() => {
                  const newPassword = generatePassword();
                  setFormData({ ...formData, password: newPassword });
                }}
              >
                Generate
              </button>
            </div>

            <div>
              <Typography>Role</Typography>
              <FormControl fullWidth>
                <Select
                  value={formData.role}
                  onChange={handleRoleChange}
                  required={true}
                >
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="member">Member</MenuItem>
                </Select>
              </FormControl>
            </div>
            {formData.role === "member" && (
              <Box className={styles.optionsContainer}>
                {formData.option.map((op, i) => (
                  <Card key={i} className={styles.optionCard} sx={{ p: 1.5 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={7}>
                        <Typography fontWeight="bold">{op.Title}</Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <FormControl fullWidth size="small">
                          <Select
                            value={op.role}
                            onChange={(e) =>
                              handleOptionRoleChange(i, e.target.value)
                            }
                          >
                            <MenuItem value="allow">Allow</MenuItem>
                            <MenuItem value="noallow">Not Allow</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                    {Array.isArray(op.items) && op.items.length > 0 && (
                      <Box
                        sx={{
                          mt: 1.5,
                          pl: 2,
                          borderLeft: "2px solid #e6eef6",
                          opacity: op.role === "allow" ? 1 : 0.55,
                        }}
                      >
                        {op.items.map((it, j) => (
                          <Grid
                            container
                            spacing={1}
                            alignItems="center"
                            key={it.to}
                            sx={{ py: 0.5 }}
                          >
                            <Grid item xs={7}>
                              <Typography
                                fontSize={13.5}
                                color={op.role === "allow" ? "text.primary" : "text.secondary"}
                              >
                                {it.label}
                              </Typography>
                            </Grid>
                            <Grid item xs={5}>
                              <FormControl fullWidth size="small">
                                <Select
                                  value={it.role}
                                  onChange={(e) =>
                                    handleItemRoleChange(i, j, e.target.value)
                                  }
                                  disabled={op.role !== "allow"}
                                >
                                  <MenuItem value="allow">Allow</MenuItem>
                                  <MenuItem value="noallow">Not Allow</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                          </Grid>
                        ))}
                      </Box>
                    )}
                  </Card>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            className={styles.actionButton}
            onClick={handleSubmit}
            disabled={postLoader}
          >
            {postLoader ? (
              <CircularProgress size={25} color="inherit" />
            ) : (
              "Invite User"
            )}
          </Button>
          <Button
            className={styles.closeButton}
            onClick={inviteUserCloseHandler}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InviteDialog;
